import {
  ReactNode,
  cloneElement,
  isValidElement,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Portal } from 'components/Portal/Portal';
import { useHideDocumentScroll } from 'hooks/useHideDocumentScroll';

import { DefaultTooltipButton } from './DefaultTooltipButton';
import {
  TargetContainer,
  TooltipCloseButton,
  TooltipContainer,
  TooltipOverlay,
  TooltipWrapper,
} from './Tooltip.styled';
import { TooltipPosition } from './TooltipPosition';
import { TooltipSize } from './TooltipSize';
import { TooltipVariant } from './TooltipVariant';

type Props = {
  children?: ReactNode;
  closeable?: boolean;
  content: ReactNode;
  className?: string;
  placement?: TooltipPosition;
  triggerCondition?: boolean;
  size?: TooltipSize;
  variant?: TooltipVariant;
  withOverlay?: boolean;
};

export function Tooltip({
  placement = 'top-center',
  children,
  closeable,
  content,
  className,
  triggerCondition,
  size = 'small',
  variant = 'brand-blue',
  withOverlay = false,
}: Props) {
  const [showTooltip, setShowTooltip] = useState(triggerCondition);
  const childRef = useRef<HTMLElement>(null);
  const [targetWidth, setTargetWidth] = useState<number>(0);
  const [targetHeight, setTargetHeight] = useState<number>(0);

  useHideDocumentScroll({ show: !showTooltip });

  useEffect(() => {
    setShowTooltip(triggerCondition);
  }, [triggerCondition]);

  useEffect(() => {
    if (!targetWidth && !targetHeight && childRef.current) {
      const { width, height } = childRef.current.getBoundingClientRect();

      setTargetWidth(width);
      setTargetHeight(height);
    }
  }, [targetWidth, targetHeight, showTooltip, childRef]);

  const childrenToClone = isValidElement(children) ? (
    children
  ) : (
    <DefaultTooltipButton />
  );

  return (
    <TargetContainer>
      {cloneElement(childrenToClone, {
        ref: childRef,
        onMouseOver:
          triggerCondition === undefined
            ? () => setShowTooltip(true)
            : undefined,
        onMouseLeave: closeable ? undefined : () => setShowTooltip(false),
      })}
      {showTooltip ? (
        <TooltipContainer
          className={className}
          $targetWidth={targetWidth}
          $targetHeight={targetHeight}
          $placement={placement}
          $variant={variant}
          $size={size}
        >
          <TooltipWrapper $size={size}>
            {closeable ? (
              <TooltipCloseButton
                onClick={() => setShowTooltip(false)}
                data-qa-id="close-modal-button"
                data-modal-focus="false"
              />
            ) : null}
            {content}
          </TooltipWrapper>
          {withOverlay ? (
            <Portal>
              <TooltipOverlay />
            </Portal>
          ) : null}
        </TooltipContainer>
      ) : null}
    </TargetContainer>
  );
}
